import dayjs from 'dayjs';
import {
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
  when,
} from 'mobx';

import type {ConferenceApiModel} from '../../api/conference';
import type {CourseExpandedCommon} from '../../api/course';
import type {Destructible} from '../../utils/store';
import type {TimeslotModel} from './Timeslot.model';
import type {BookingPeriod, DataModel, MarkedDate, MarkedDates} from './types';

import {allOf, not} from '../../utils/conditionalUtils';
import {dateTimeObjToTimestamp} from '../../utils/datetime';
import {
  getCourseDisplayFields,
  isCourseArchived,
  isCourseCorporate,
  isCourseEvergreen,
  isCourseIndividual,
} from '../../utils/entity/course';
import {getFileSrc} from '../../utils/entity/file';
import {isMeCoachForEntity} from '../../utils/entity/isMeCoachForEntity';
import {envManager} from '../../utils/env';
import {AsyncBusyManager, ReactionDisposeManager} from '../../utils/store';
import {isDefined} from '../../utils/typecheck';
import {DATE_KEY_FORMAT} from './utils/constants';
import {
  getBookedConferences,
  getBookedIntroConferences,
} from './utils/getBookedConferences';
import {
  type ConferenceLimits,
  getConferenceLimits,
} from './utils/getConferenceLimits';
import {getPeriods} from './utils/getPeriods';

type BusyKey = 'fetchBookedConferences';

export class CourseModel implements DataModel, Destructible {
  busy = new AsyncBusyManager<BusyKey>();
  disposer = new ReactionDisposeManager();
  entity: CourseExpandedCommon;
  private bookedConferences: ConferenceApiModel[] = [];
  private periodsWithAvailability: BookingPeriod[] = [];

  constructor(course: CourseExpandedCommon) {
    this.entity = course;

    this.busy.observe<CourseModel, 'fetchBookedConferences'>(this, {
      fetchBookedConferences: 'fetchBookedConferences',
    });

    makeAutoObservable<
      CourseModel,
      'bookedConferences' | 'periodsWithAvailability'
    >(
      this,
      {
        bookedConferences: observable.ref,
        entity: observable.ref,
        periodsWithAvailability: observable.ref,
      },
      {
        autoBind: true,
      },
    );

    this.disposer.watch(
      'fetch_booked',
      when(
        () => isDefined(this.entity),
        () => {
          this.fetchBookedConferences();
        },
      ),
    );

    this.disposer.watch(
      'process_booked_conference',
      reaction(
        () => this.bookedConferences,
        () => {
          this.periodsWithAvailability = this.getPeriodsWithAvailability();
        },
      ),
    );
  }

  destruct() {
    this.disposer.dispose();
  }

  get availableConferences() {
    if (this.busy.getReason('fetchBookedConferences')) {
      return 0;
    }

    const {perPeriod} = this.conferenceLimits;

    const availableMain = this.periods.reduce((sum, period) => {
      const bookedInPeriod = this.bookedConferences.filter(conference => {
        if (conference.billing_type === 'intro') {
          return false;
        }

        const conferenceDate = dateTimeObjToTimestamp(conference.start_date);

        return conferenceDate >= period.start && conferenceDate <= period.end;
      }).length;

      return sum + Math.max(0, perPeriod - bookedInPeriod);
    }, 0);

    return availableMain + this.availableIntroConferences;
  }

  get availableIntroConferences() {
    return this.getAvailableIntroConferences();
  }

  get avatarUrl() {
    return getFileSrc(this.entity.program.avatar).url;
  }

  get bookedConferencesAmount() {
    return this.bookedConferences.filter(
      conference => conference.billing_type !== 'intro',
    ).length;
  }

  get bookedIntroConferencesAmount() {
    return this.getBookedIntroConferencesAmount();
  }

  get bookingRange() {
    const today = Date.now();
    const startDate = this.entity.start_date
      ? dateTimeObjToTimestamp(this.entity.start_date)
      : today;
    const courseEndDate = this.entity.end_date
      ? dateTimeObjToTimestamp(this.entity.end_date)
      : today;
    const endDate = this.datesOfLastPeriod?.end || courseEndDate;

    return {
      end: endDate,
      start: startDate,
    };
  }

  get canBookConference() {
    return this.shouldShowBookButton && this.isBookingAvailableInAnyPeriod;
  }

  get conferenceCount() {
    return this.conferenceLimits.total;
  }

  get conferenceDuration() {
    const clientConferences = getCourseDisplayFields(
      this.entity,
    ).clientConferences;
    const durationInEdition = clientConferences?.duration ?? 0;

    if (envManager.isB2b) {
      return this.entity.subtenant?.session_duration || durationInEdition;
    }

    return durationInEdition;
  }

  get conferenceLimits(): ConferenceLimits {
    return getConferenceLimits(this.entity);
  }

  get datesOfLastPeriod() {
    return this.periods[this.periods.length - 1];
  }

  get hasIntroAvailability() {
    return this.availableIntroConferences > 0;
  }

  get id() {
    return this.entity._id;
  }

  get isBookButtonDisabled() {
    return (
      Boolean(this.busy.getReason('fetchBookedConferences')) ||
      this.availableConferences <= 0
    );
  }

  get isBookingAvailableInAnyPeriod() {
    return this.periodsWithAvailability.some(period => period.isAvailable);
  }

  get isCourseCorporate() {
    return isCourseCorporate(this.entity);
  }

  get isCourseEvergreen() {
    return isCourseEvergreen(this.entity);
  }

  get periods() {
    return getPeriods(this.entity);
  }

  get shouldShowBookButton() {
    return allOf([
      isCourseIndividual(this.entity),
      not(isMeCoachForEntity(this.entity)),
      this.conferenceCount !== null,
      this.entity?.status === 'ongoing',
    ]);
  }

  get title() {
    return this.entity.program.title;
  }

  /**
   * Identifies and categorizes dates as available or unavailable for booking within specific periods.
   *
   * @param {Map<string, TimeslotModel[][]>} timeslots - A map where key is date (YYYY-MM-DD) that need to be checked for availability.
   * @param {string} conferenceToRescheduleId - If it's rescheduling of conference we should not count it in a period
   * @return {Object} An object containing two sets: `availableDates` and `unavailableDates` (YYYY-MM-DD).
   */
  getMarkedDates(
    timeslots: Map<string, TimeslotModel[][]>,
    conferenceToRescheduleId?: string,
  ): MarkedDates {
    const available = new Map<string, MarkedDate>();
    const unavailable = new Map<string, MarkedDate>();

    if (!timeslots.size) {
      return {available, unavailable};
    }

    const periods = this.getPeriodsWithAvailability(conferenceToRescheduleId);

    periods.forEach(period => {
      const isPeriodAvailableForBooking = period.isAvailable;

      for (
        let date = dayjs(period.start);
        !date.isAfter(period.end);
        date = date.add(1, 'days')
      ) {
        const formattedDate = date.format(DATE_KEY_FORMAT);
        const isFirstDay = date.isSame(period.start, 'day');
        const isLastDay = date.isSame(period.end, 'day');

        if (timeslots.has(formattedDate)) {
          let isDateAvailable = false;

          if (isPeriodAvailableForBooking) {
            // Should take only timeslots of current period for the first and last date
            // because period can start at any time of the day
            if (isFirstDay) {
              const lastTimeSlot = this.getLastTimeslot(
                timeslots,
                formattedDate,
              );

              isDateAvailable =
                lastTimeSlot?.startDateDjs.isAfter(date) ?? false;
            } else if (isLastDay) {
              const firstTimeSlot = this.getFirstTimeslot(
                timeslots,
                formattedDate,
              );

              isDateAvailable =
                firstTimeSlot?.startDateDjs.isBefore(date) ?? false;
            } else {
              isDateAvailable = true;
            }
          }

          if (isDateAvailable) {
            // If we already have the date from prev period, it means the whole day is available
            if (available.has(formattedDate)) {
              available.set(formattedDate, {});
            } else {
              available.set(formattedDate, {
                from: isFirstDay ? period.start : undefined,
                to: isLastDay ? period.end : undefined,
              });
            }
          } else {
            unavailable.set(formattedDate, {});
          }

          // If the date is available in any period, we should remove it from unavailable set
          if (available.has(formattedDate)) {
            unavailable.delete(formattedDate);
          }
        }
      }
    });

    return {available, unavailable};
  }

  async refresh() {
    await this.fetchBookedConferences();
  }

  private async fetchBookedConferences() {
    if (isCourseArchived(this.entity)) {
      return;
    }

    const [mainConferences, introConferences] = await Promise.all([
      getBookedConferences(this.entity, {
        end: this.datesOfLastPeriod!.end,
        start: this.periods[0]!.start,
      }),
      getBookedIntroConferences(this.entity),
    ]);

    runInAction(() => {
      this.bookedConferences = [...mainConferences, ...introConferences];
    });
  }

  private getAvailableIntroConferences(conferenceToRescheduleId?: string) {
    return Math.max(
      0,
      this.conferenceLimits.introTotal -
        this.getBookedIntroConferencesAmount(conferenceToRescheduleId),
    );
  }

  private getBookedIntroConferencesAmount(conferenceToRescheduleId?: string) {
    let count = 0;

    for (const conference of this.bookedConferences) {
      if (
        conference.billing_type === 'intro' &&
        conference._id !== conferenceToRescheduleId
      ) {
        count += 1;
      }
    }

    return count;
  }

  private getFirstTimeslot(
    timeslots: Map<string, TimeslotModel[][]>,
    formattedDate: string,
  ) {
    return timeslots.get(formattedDate)?.[0]?.[0] ?? null;
  }

  private getLastTimeslot(
    timeslots: Map<string, TimeslotModel[][]>,
    formattedDate: string,
  ) {
    const currentTimeslots = timeslots.get(formattedDate);

    if (!currentTimeslots?.length) {
      return null;
    }

    const timeslotsRow = currentTimeslots[currentTimeslots.length - 1];

    return timeslotsRow?.[timeslotsRow.length - 1] ?? null;
  }

  private getPeriodsWithAvailability(conferenceToRescheduleId?: string) {
    const {perPeriod} = this.conferenceLimits;
    const hasIntroAvailability =
      this.getAvailableIntroConferences(conferenceToRescheduleId) > 0;

    const periodsWithAvailability = this.periods.map(period => {
      const conferences = this.bookedConferences.filter(conference => {
        if (conference.billing_type === 'intro') {
          return false;
        }

        const conferenceDate = dateTimeObjToTimestamp(conference.start_date);

        if (conference._id === conferenceToRescheduleId) {
          return false;
        }

        return conferenceDate >= period.start && conferenceDate <= period.end;
      });

      /**
       * Intro capacity belongs to the whole course, not a single period.
       * Backend treats the first eligible booking as an intro call, so the
       * calendar must stay selectable while intro capacity remains even when
       * regular per-period capacity is zero or already full.
       */
      const isPeriodAvailableForBooking =
        hasIntroAvailability || perPeriod > conferences.length;

      return {
        ...period,
        isAvailable: isPeriodAvailableForBooking,
      };
    });

    return periodsWithAvailability;
  }
}
